import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ShareableLink {
  id: string;
  name: string;
  url: string;
  shortCode: string;
  createdAt: Date;
  expiresAt: Date | null;
  maxUploads: number | null;
  uploadsReceived: number;
  totalSize: number;
  isActive: boolean;
  allowedFileTypes: string[];
  maxFileSize: number;
  requirePassword: boolean;
  uploads: Upload[];
}

interface Upload {
  id: string;
  fileName: string;
  fileSize: number;
  uploadedAt: Date;
  uploaderInfo: string;
  ipAddress: string;
  status: 'completed' | 'failed' | 'processing';
}

interface StepInfo {
  step: number;
  title: string;
  description: string;
  status: 'pending' | 'active' | 'completed' | 'error';
  details?: string;
}

const ShareableLinkManager: React.FC = () => {
  const [links, setLinks] = useState<ShareableLink[]>([]);
  const [newLinkName, setNewLinkName] = useState('');
  const [selectedLink, setSelectedLink] = useState<ShareableLink | null>(null);
  const [activeTab, setActiveTab] = useState<'create' | 'manage' | 'uploads' | 'activity'>('create');
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState<StepInfo[]>([]);
  const [showSteps, setShowSteps] = useState(false);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [recentActivity, setRecentActivity] = useState<string[]>([]);
  const [uploadNotifications, setUploadNotifications] = useState<{id: string; message: string}[]>([]);
  const [linkSettings, setLinkSettings] = useState({
    expiresIn: 7, // days
    maxUploads: 100,
    maxFileSize: 100, // MB
    requirePassword: false,
    password: '',
    allowedTypes: ['*'],
    notifyOnUpload: true,
    autoDelete: false,
  });

  // Initialize creation steps
  const initializeSteps = (linkName: string): StepInfo[] => [
    {
      step: 1,
      title: 'Validate Link Settings',
      description: `LOCAL: Checking link name "${linkName}", expiration (${linkSettings.expiresIn} days), file limits (${linkSettings.maxFileSize}MB)`,
      status: 'pending',
      details: 'Browser validation - instant (<1ms)',
    },
    {
      step: 2,
      title: 'Generate Unique Short Code',
      description: 'LOCAL: Creating random alphanumeric code like "a7b9x2m". Checking for collisions.',
      status: 'pending',
      details: 'Math.random() + base36 encoding',
    },
    {
      step: 3,
      title: 'Save to Database',
      description: `NETWORK: POST /api/links → CloudFlare D1. INSERT INTO shareable_links with ID, settings, timestamps.`,
      status: 'pending',
      details: 'Database write ~20-50ms',
    },
    {
      step: 4,
      title: 'Create Upload Directory',
      description: `STORAGE: Creating folder in R2 at path /uploads/{linkId}/. Setting permissions and metadata.`,
      status: 'pending',
      details: 'CloudFlare R2 API call ~30-100ms',
    },
    {
      step: 5,
      title: 'Generate Shareable URL',
      description: `LOCAL: Building URL like https://yoursite.com/upload/a7b9x2m. Ready to share!`,
      status: 'pending',
      details: 'String concatenation - instant',
    },
  ];

  // Log each step with details
  const logStep = (step: number, status: StepInfo['status'], error?: string) => {
    setSteps(prev => prev.map(s =>
      s.step === step ? { ...s, status, details: error || s.details } : s
    ));
  };

  // Log activity
  const logActivity = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setRecentActivity(prev => [`[${timestamp}] ${message}`, ...prev].slice(0, 50));
  };

  // Simulate receiving an upload notification
  const simulateUploadNotification = (linkId: string, linkName: string) => {
    const fileName = `document_${Math.random().toString(36).substr(2, 6)}.pdf`;
    const fileSize = Math.floor(Math.random() * 50) + 1; // 1-50 MB
    const uploaderEmail = `user${Math.floor(Math.random() * 999)}@example.com`;

    const notification = {
      id: Math.random().toString(36),
      message: `📁 New upload to "${linkName}": ${fileName} (${fileSize}MB) from ${uploaderEmail}`,
    };

    setUploadNotifications(prev => [notification, ...prev].slice(0, 5));
    logActivity(`Upload received: ${fileName} → ${linkName}`);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setUploadNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);

    // Update link stats
    setLinks(prev => prev.map(link => {
      if (link.id === linkId) {
        const newUpload: Upload = {
          id: Math.random().toString(36),
          fileName,
          fileSize: fileSize * 1024 * 1024,
          uploadedAt: new Date(),
          uploaderInfo: uploaderEmail,
          ipAddress: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
          status: 'completed',
        };
        return {
          ...link,
          uploadsReceived: link.uploadsReceived + 1,
          totalSize: link.totalSize + newUpload.fileSize,
          uploads: [newUpload, ...link.uploads].slice(0, 100),
        };
      }
      return link;
    }));
  };

  // Poll for uploads simulation (in real app, would use WebSocket)
  useEffect(() => {
    const interval = setInterval(() => {
      if (links.length > 0 && Math.random() < 0.3) { // 30% chance every 5 seconds
        const activeLinks = links.filter(l => l.isActive);
        if (activeLinks.length > 0) {
          const randomLink = activeLinks[Math.floor(Math.random() * activeLinks.length)];
          simulateUploadNotification(randomLink.id, randomLink.name);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [links]);

  // Create shareable link
  const createLink = async () => {
    if (!newLinkName.trim()) {
      alert('Please enter a name for this upload link');
      return;
    }

    setLoading(true);
    setShowSteps(true);
    const newSteps = initializeSteps(newLinkName);
    setSteps(newSteps);

    try {
      // Step 1: Validate locally
      logStep(1, 'active');
      await new Promise(resolve => setTimeout(resolve, 10));

      if (newLinkName.length < 3) {
        logStep(1, 'error', 'Name must be at least 3 characters');
        setLoading(false);
        return;
      }
      logStep(1, 'completed');

      // Step 2: Generate short code
      logStep(2, 'active');
      await new Promise(resolve => setTimeout(resolve, 10));

      const shortCode = Math.random().toString(36).substring(2, 9);
      console.log(`Generated short code: ${shortCode}`);
      logStep(2, 'completed');

      // Step 3: Save to database
      logStep(3, 'active');

      const response = await fetch('http://localhost:8787/api/shareable-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newLinkName,
          shortCode,
          settings: linkSettings,
          userId: 1, // Would come from auth
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        logStep(3, 'error', `Database error: ${data.error || response.statusText}`);
        console.error('Failed to create link:', data);
        alert(`Failed: ${data.error || 'Could not save link'}`);
        setLoading(false);
        return;
      }

      logStep(3, 'completed');

      // Step 4: Create upload directory
      logStep(4, 'active');
      await new Promise(resolve => setTimeout(resolve, 50)); // Simulate R2 call
      logStep(4, 'completed');

      // Step 5: Generate URL
      logStep(5, 'active');
      const shareableUrl = `${window.location.origin}/upload/${shortCode}`;

      const newLink: ShareableLink = {
        id: data.id || shortCode,
        name: newLinkName,
        url: shareableUrl,
        shortCode,
        createdAt: new Date(),
        expiresAt: linkSettings.expiresIn ?
          new Date(Date.now() + linkSettings.expiresIn * 24 * 60 * 60 * 1000) : null,
        maxUploads: linkSettings.maxUploads,
        uploadsReceived: 0,
        totalSize: 0,
        isActive: true,
        allowedFileTypes: linkSettings.allowedTypes,
        maxFileSize: linkSettings.maxFileSize * 1024 * 1024, // Convert MB to bytes
        requirePassword: linkSettings.requirePassword,
        uploads: [],
      };

      setLinks(prev => [newLink, ...prev]);
      logStep(5, 'completed');
      logActivity(`Created shareable link: ${newLinkName} (${shortCode})`);

      // Auto-copy to clipboard
      navigator.clipboard.writeText(shareableUrl);
      setCopiedLink(shareableUrl);
      setTimeout(() => setCopiedLink(null), 3000);

      // Reset form
      setNewLinkName('');
      setSelectedLink(newLink);
      setActiveTab('manage');

    } catch (error) {
      console.error('Error creating link:', error);
      alert(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
      setTimeout(() => setShowSteps(false), 5000);
    }
  };

  // Copy link to clipboard
  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(url);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  // Toggle link active status
  const toggleLink = async (linkId: string) => {
    try {
      const response = await fetch(`http://localhost:8787/api/shareable-links/${linkId}/toggle`, {
        method: 'POST',
      });

      if (response.ok) {
        setLinks(prev => prev.map(link =>
          link.id === linkId ? { ...link, isActive: !link.isActive } : link
        ));
      }
    } catch (error) {
      console.error('Error toggling link:', error);
    }
  };

  // Delete link
  const deleteLink = async (linkId: string) => {
    if (!confirm('Delete this link and all associated uploads?')) return;

    try {
      const response = await fetch(`http://localhost:8787/api/shareable-links/${linkId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setLinks(prev => prev.filter(link => link.id !== linkId));
        setSelectedLink(null);
      }
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2">🔗 Shareable Upload Links</h1>
          <p className="text-gray-400">Create secure links that let anyone upload files to your storage</p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-400">{links.length}</div>
            <div className="text-sm text-gray-400">Active Links</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-400">
              {links.reduce((sum, link) => sum + link.uploadsReceived, 0)}
            </div>
            <div className="text-sm text-gray-400">Total Uploads</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-400">
              {(links.reduce((sum, link) => sum + link.totalSize, 0) / 1024 / 1024).toFixed(1)} MB
            </div>
            <div className="text-sm text-gray-400">Storage Used</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-400">
              {links.filter(l => l.isActive).length}
            </div>
            <div className="text-sm text-gray-400">Active Now</div>
          </div>
        </div>

        {/* Upload Notifications */}
        <AnimatePresence>
          {uploadNotifications.length > 0 && (
            <div className="fixed top-4 right-4 z-50 space-y-2">
              {uploadNotifications.map((notif) => (
                <motion.div
                  key={notif.id}
                  initial={{ x: 300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 300, opacity: 0 }}
                  className="bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg max-w-md"
                >
                  {notif.message}
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('create')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'create' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            ➕ Create Link
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'manage' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            📊 Manage Links
          </button>
          <button
            onClick={() => setActiveTab('uploads')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'uploads' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            📁 Recent Uploads
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'activity' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            📡 Activity Log
          </button>
        </div>

        {/* Create Tab */}
        {activeTab === 'create' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Create New Upload Link</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Link Name/Description</label>
                  <input
                    type="text"
                    value={newLinkName}
                    onChange={(e) => setNewLinkName(e.target.value)}
                    placeholder="e.g., Client Project Files, Wedding Photos"
                    className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Expires In (days)</label>
                    <input
                      type="number"
                      value={linkSettings.expiresIn}
                      onChange={(e) => setLinkSettings({...linkSettings, expiresIn: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Max Uploads</label>
                    <input
                      type="number"
                      value={linkSettings.maxUploads || ''}
                      onChange={(e) => setLinkSettings({...linkSettings, maxUploads: parseInt(e.target.value) || 0})}
                      placeholder="Unlimited"
                      className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Max File Size (MB)</label>
                  <input
                    type="number"
                    value={linkSettings.maxFileSize}
                    onChange={(e) => setLinkSettings({...linkSettings, maxFileSize: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={linkSettings.requirePassword}
                      onChange={(e) => setLinkSettings({...linkSettings, requirePassword: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span>Require Password</span>
                  </label>

                  {linkSettings.requirePassword && (
                    <input
                      type="password"
                      value={linkSettings.password}
                      onChange={(e) => setLinkSettings({...linkSettings, password: e.target.value})}
                      placeholder="Enter password"
                      className="flex-1 px-4 py-2 bg-gray-900 rounded-lg border border-gray-700"
                    />
                  )}
                </div>

                <button
                  onClick={createLink}
                  disabled={loading || !newLinkName.trim()}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-bold hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 transition"
                >
                  {loading ? 'Creating Link...' : 'Create Shareable Link'}
                </button>
              </div>
            </div>

            {/* Step Visualization */}
            <AnimatePresence>
              {showSteps && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-gray-800 rounded-lg p-6 space-y-3"
                >
                  <h3 className="text-lg font-bold mb-4">Creation Steps:</h3>
                  {steps.map((step) => (
                    <motion.div
                      key={step.step}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: step.step * 0.1 }}
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        step.status === 'completed' ? 'bg-green-900/20' :
                        step.status === 'active' ? 'bg-blue-900/20' :
                        step.status === 'error' ? 'bg-red-900/20' :
                        'bg-gray-700/20'
                      }`}
                    >
                      <div className={`mt-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        step.status === 'completed' ? 'bg-green-500' :
                        step.status === 'active' ? 'bg-blue-500 animate-pulse' :
                        step.status === 'error' ? 'bg-red-500' :
                        'bg-gray-600'
                      }`}>
                        {step.status === 'completed' ? '✓' :
                         step.status === 'error' ? '✗' :
                         step.step}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{step.title}</div>
                        <div className="text-sm text-gray-400">{step.description}</div>
                        {step.details && (
                          <div className="text-xs text-gray-500 mt-1">{step.details}</div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Manage Tab */}
        {activeTab === 'manage' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {links.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-8 text-center">
                <p className="text-gray-400 mb-4">No shareable links created yet</p>
                <button
                  onClick={() => setActiveTab('create')}
                  className="px-6 py-2 bg-blue-600 rounded-lg font-bold hover:bg-blue-700"
                >
                  Create Your First Link
                </button>
              </div>
            ) : (
              links.map((link) => (
                <div key={link.id} className="bg-gray-800 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{link.name}</h3>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          link.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {link.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-sm text-gray-400">
                          {link.uploadsReceived} uploads • {(link.totalSize / 1024 / 1024).toFixed(1)} MB
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyLink(link.url)}
                        className="px-3 py-1 bg-blue-600 rounded text-sm font-bold hover:bg-blue-700"
                      >
                        {copiedLink === link.url ? 'Copied!' : 'Copy Link'}
                      </button>
                      <button
                        onClick={() => toggleLink(link.id)}
                        className="px-3 py-1 bg-gray-700 rounded text-sm font-bold hover:bg-gray-600"
                      >
                        {link.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => deleteLink(link.id)}
                        className="px-3 py-1 bg-red-600 rounded text-sm font-bold hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-900 rounded p-3 mb-3">
                    <code className="text-sm text-blue-400 break-all">{link.url}</code>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Created:</span>
                      <div>{new Date(link.createdAt).toLocaleDateString()}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Expires:</span>
                      <div>{link.expiresAt ? new Date(link.expiresAt).toLocaleDateString() : 'Never'}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Max Size:</span>
                      <div>{link.maxFileSize / 1024 / 1024} MB</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Password:</span>
                      <div>{link.requirePassword ? 'Yes' : 'No'}</div>
                    </div>
                  </div>

                  {link.uploads.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-semibold mb-2">Recent Uploads:</h4>
                      <div className="space-y-1">
                        {link.uploads.slice(0, 3).map((upload) => (
                          <div key={upload.id} className="flex justify-between text-sm">
                            <span>{upload.fileName}</span>
                            <span className="text-gray-400">
                              {(upload.fileSize / 1024 / 1024).toFixed(1)} MB • {new Date(upload.uploadedAt).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </motion.div>
        )}

        {/* Uploads Tab */}
        {activeTab === 'uploads' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4">📁 Recent Uploads Across All Links</h2>

              {links.flatMap(l => l.uploads).length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">📭</div>
                  <p className="text-gray-400 mb-2">No uploads received yet</p>
                  <p className="text-sm text-gray-500">Share your links to start receiving files</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {links.flatMap(link =>
                    link.uploads.map(upload => ({
                      ...upload,
                      linkName: link.name,
                      linkId: link.id,
                    }))
                  )
                  .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())
                  .slice(0, 20)
                  .map((upload) => (
                    <motion.div
                      key={upload.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gray-900 rounded-lg p-4 hover:bg-gray-850 transition"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">
                            {upload.fileName.endsWith('.pdf') ? '📄' :
                             upload.fileName.endsWith('.jpg') || upload.fileName.endsWith('.png') ? '🖼️' :
                             upload.fileName.endsWith('.zip') ? '📦' : '📎'}
                          </div>
                          <div>
                            <div className="font-semibold">{upload.fileName}</div>
                            <div className="text-sm text-gray-400">
                              To: {upload.linkName} • From: {upload.uploaderInfo || upload.ipAddress}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold">{(upload.fileSize / 1024 / 1024).toFixed(1)} MB</div>
                          <div className="text-xs text-gray-400">
                            {new Date(upload.uploadedAt).toLocaleString()}
                          </div>
                          <div className={`text-xs mt-1 px-2 py-1 rounded-full inline-block ${
                            upload.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            upload.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {upload.status}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gray-800 rounded-lg p-6"
          >
            <h2 className="text-2xl font-bold mb-4">📡 Real-Time Activity Log</h2>

            {recentActivity.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🔍</div>
                <p className="text-gray-400">No activity yet</p>
                <p className="text-sm text-gray-500">Actions and events will appear here</p>
              </div>
            ) : (
              <div className="bg-gray-900 rounded-lg p-4 max-h-96 overflow-y-auto">
                <div className="space-y-1 font-mono text-sm">
                  {recentActivity.map((activity, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.01 }}
                      className="text-gray-300 hover:text-white hover:bg-gray-800 px-2 py-1 rounded transition"
                    >
                      {activity}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setRecentActivity([])}
                className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition"
              >
                Clear Log
              </button>
              <button
                onClick={() => {
                  const log = recentActivity.join('\n');
                  navigator.clipboard.writeText(log);
                  alert('Activity log copied to clipboard');
                }}
                className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition"
              >
                Copy Log
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ShareableLinkManager;